import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiCall } from '@/utils/api';

interface AllocationCell {
  allocationId: string;
  teamWeeks: number;
  cost: number;
}

interface EpicWithAllocations {
  id: string;
  name: string;
  priority: number | null;
  status: string;
  requestedTeamWeeks: number;
  allocatedTeamWeeks: number;
  variance: number;
  allocations: {
    [programId: string]: {
      [teamId: string]: AllocationCell;
    };
  };
}

interface ProgramWithTeams {
  id: string;
  name: string;
  category: string;
  teams: Array<{
    id: string;
    name: string;
    memberCount: number;
    globalAllocatedTWs: number;
  }>;
}

interface InvestibleStructureInfo {
  id: string;
  name: string;
  category: string;
  totalDemand: number;
  totalSupply: number;
}

interface ISAllocationGridState {
  selectedISId: string | null;
  scenarioId: string | null;
  investibleStructure: InvestibleStructureInfo | null;
  epics: EpicWithAllocations[];
  programs: ProgramWithTeams[];
  summary: {
    totalDemand: number;
    totalSupply: number;
    variance: number;
    totalCost: number;
  };
  pendingUpdates: Record<string, number>; // allocationId -> teamWeeks
  dirtyCells: Record<string, boolean>;    // cellKey -> isDirty
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

const initialState: ISAllocationGridState = {
  selectedISId: null,
  scenarioId: null,
  investibleStructure: null,
  epics: [],
  programs: [],
  summary: {
    totalDemand: 0,
    totalSupply: 0,
    variance: 0,
    totalCost: 0
  },
  pendingUpdates: {},
  dirtyCells: {},
  isLoading: false,
  isSaving: false,
  error: null
};

// Async thunks
export const fetchISAllocations = createAsyncThunk(
  'isAllocationGrid/fetch',
  async ({ isId, scenarioId }: { isId: string; scenarioId?: string | null }) => {
    const params = new URLSearchParams();
    if (scenarioId) params.append('scenarioId', scenarioId);

    const response = await apiCall('GET', `/investible-structures/${isId}/allocations?${params}`);
    // Backend returns { success: true, data: { investibleStructure, epics, programs, summary } }
    // Extract the actual data object
    return response.data?.data || response.data;
  }
);

export const bulkSaveAllocations = createAsyncThunk(
  'isAllocationGrid/bulkSave',
  async (updates: Array<{ id: string; teamWeeks: number }>) => {
    const response = await apiCall('PUT', '/epic-allocations/bulk-update', { updates });
    // Backend returns { success: true, data: [...] }
    // Extract the actual data
    return response.data?.data || response.data;
  }
);

export const createAllocation = createAsyncThunk(
  'isAllocationGrid/create',
  async (data: {
    epicId: string;
    programId: string;
    teamId: string;
    allocatedTeamWeeks: number;
    quarter?: number;
  }) => {
    const { epicId, ...body } = data;
    const response = await apiCall('POST', `/epics/${epicId}/allocations`, body);
    return response.data?.data || response.data;
  }
);

// Slice
const isAllocationGridSlice = createSlice({
  name: 'isAllocationGrid',
  initialState,
  reducers: {
    setSelectedIS(state, action: PayloadAction<string | null>) {
      state.selectedISId = action.payload;
    },

    setScenarioId(state, action: PayloadAction<string | null>) {
      state.scenarioId = action.payload;
    },

    updateCellValue(state, action: PayloadAction<{
      epicId: string;
      programId: string;
      teamId: string;
      value: number;
    }>) {
      const { epicId, programId, teamId, value } = action.payload;
      const epic = state.epics.find(e => e.id === epicId);

      if (epic?.allocations[programId]?.[teamId]) {
        const allocationId = epic.allocations[programId][teamId].allocationId;

        // Optimistic update
        epic.allocations[programId][teamId].teamWeeks = value;

        // Track pending update
        state.pendingUpdates[allocationId] = value;

        // Mark dirty
        const cellKey = `${epicId}-${programId}-${teamId}`;
        state.dirtyCells[cellKey] = true;

        // Recalculate epic totals
        epic.allocatedTeamWeeks = Object.values(epic.allocations).reduce((sum, teams) => {
          return sum + Object.values(teams).reduce((tSum, alloc) => tSum + alloc.teamWeeks, 0);
        }, 0);
        epic.variance = epic.requestedTeamWeeks - epic.allocatedTeamWeeks;

        // Recalculate summary
        state.summary.totalSupply = state.epics.reduce((sum, e) => sum + e.allocatedTeamWeeks, 0);
        state.summary.variance = state.summary.totalDemand - state.summary.totalSupply;
      }
    },

    clearPendingUpdates(state) {
      state.pendingUpdates = {};
      state.dirtyCells = {};
    }
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchISAllocations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchISAllocations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.investibleStructure = action.payload.investibleStructure;
        state.epics = action.payload.epics;
        state.programs = action.payload.programs;
        state.summary = action.payload.summary;
        state.pendingUpdates = {};
        state.dirtyCells = {};
      })
      .addCase(fetchISAllocations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch allocations';
      })
      .addCase(bulkSaveAllocations.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(bulkSaveAllocations.fulfilled, (state) => {
        state.isSaving = false;
        state.pendingUpdates = {};
        state.dirtyCells = {};
      })
      .addCase(bulkSaveAllocations.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.error.message || 'Failed to save allocations';
      })
      .addCase(createAllocation.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(createAllocation.fulfilled, (state) => {
        state.isSaving = false;
      })
      .addCase(createAllocation.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.error.message || 'Failed to create allocation';
      });
  }
});

export const {
  setSelectedIS,
  setScenarioId,
  updateCellValue,
  clearPendingUpdates
} = isAllocationGridSlice.actions;

export default isAllocationGridSlice.reducer;
